import React, { useState } from 'react';
import { Add, Star } from '@mui/icons-material';
import { FormInput } from 'components/form';
import { Button, Chip, IconButton, Stack, Typography } from '@mui/material';

export interface FilterSet<T> {
  label: string;
  value: T;
}

interface Props<T> {
  currentValue: T;
  activeKey: string;
  permanentKey?: string;
  onSwitch: (key: string) => void;
  onDelete: (key: string) => void;
  sets: Record<string, FilterSet<T>>;
  onAdd: (name: string, value: T) => void;
}

export const FilterSets = <T,>({
  sets,
  activeKey,
  permanentKey = 'my',
  currentValue,
  onSwitch,
  onDelete,
  onAdd,
}: Props<T>) => {
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState('');

  const handleAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onAdd(trimmed, currentValue);
    setIsAdding(false);
    setName('');
  };

  const handleCancel = () => {
    setIsAdding(false);
    setName('');
  };

  return (
    <Stack direction="row" gap="0.5rem" flexWrap="wrap" alignItems="center">
      {Object.entries(sets).map(([key, { label }]) => (
        <Chip
          key={key}
          onClick={() => onSwitch(key)}
          label={<Typography>{label}</Typography>}
          color={activeKey === key ? 'primary' : 'default'}
          icon={activeKey === key ? <Star fontSize="small" /> : undefined}
          onDelete={key !== permanentKey ? () => onDelete(key) : undefined}
        />
      ))}

      {isAdding ? (
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <FormInput
            autoFocus
            value={name}
            placeholder="Filter name"
            onChange={value => setName(value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') handleCancel();
            }}
          />

          <Button size="small" variant="contained" onClick={handleAdd} disabled={!name.trim()}>
            Save
          </Button>

          <Button size="small" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      ) : (
        <IconButton size="small" onClick={() => setIsAdding(true)} title="Add filter set">
          <Add fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
};
